import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminOrgs } from "../../hooks/use-admin.js";
import { Skeleton } from "../../components/skeleton.js";

function statusBadgeClass(
  status: string | null,
): "badge--pass" | "badge--advisory" | "badge--warning" | "badge--fail" | "badge--muted" {
  switch (status) {
    case "active":
      return "badge--pass";
    case "trialing":
      return "badge--advisory";
    case "past_due":
      return "badge--warning";
    case "canceled":
      return "badge--fail";
    default:
      return "badge--muted";
  }
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-IE");
}

export function AdminOrgsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminOrgs({
    page,
    limit: 50,
    search: search || undefined,
  });

  return (
    <div className="product-page">
      <h1 className="page-title">Organisations</h1>
      <input
        type="search"
        placeholder="Search by org name\u2026"
        aria-label="Search organisations by name"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="admin-search mb-3"
      />
      {isLoading && <Skeleton lines={3} />}
      {data && (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Country</th>
                <th>Plan</th>
                <th>Status</th>
                <th className="text-right">Websites</th>
                <th className="text-right">Scans</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {data.orgs.map((org) => (
                <tr key={org.id}>
                  <td>
                    <Link to={`/admin/orgs/${org.id}`}>{org.name}</Link>
                  </td>
                  <td className="text-sm text-muted">{org.country}</td>
                  <td className="text-sm">{org.plan?.name || "\u2014"}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(org.subscriptionStatus)}`}>
                      {org.subscriptionStatus || "none"}
                    </span>
                  </td>
                  <td className="text-right text-sm">
                    {org.websitesUsed} / {org.websitesLimit}
                  </td>
                  <td className="text-right text-sm">
                    {org.scansUsedThisMonth} / {org.scansLimitPerMonth}
                  </td>
                  <td className="text-sm text-muted">
                    {relativeTime(org.lastActivityAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.orgs.length === 0 && (
            <p className="text-muted text-center p-4">
              No organisations found.
            </p>
          )}
          <div className="d-flex gap-2 items-center mt-3">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-muted text-sm">
              Page {page} &middot; {data.total} total
            </span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.orgs.length < 50}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
